"use client";

import { useId, useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import {
  continueNativeAppleSignUp,
  type NativeAppleSignUpNeed,
  type NativeAppleSignUpStep,
} from "@award-party/shared";
import { appleSignUpErrorKey } from "@/lib/native-apple-sign-in";

interface NativeAppleSignUpFormProps {
  needs: NativeAppleSignUpNeed[];
  pending: string;
  onTicket(ticket: string): void;
  onFailure(code: string): void;
  onCancel(): void;
}

const INPUT_CLASS =
  "h-11 w-full rounded-xl border border-(--mk-cream-08) bg-(--mk-ink) px-3 text-base text-(--mk-cream) outline-none placeholder:text-(--mk-cream-35) focus-visible:border-(--mk-gold-border) focus-visible:ring-2 focus-visible:ring-(--mk-gold)/25 disabled:cursor-not-allowed disabled:opacity-60";
const LABEL_CLASS = "mb-1.5 block text-xs font-medium text-(--mk-cream-75)";

/**
 * Collects what Apple withheld for a new account (email, name), then the
 * code Clerk emails to confirm that address.
 */
export function NativeAppleSignUpForm({
  needs: initialNeeds,
  pending: initialPending,
  onTicket,
  onFailure,
  onCancel,
}: NativeAppleSignUpFormProps) {
  const t = useTranslations("Auth");
  const titleId = useId();
  const fieldId = useId();
  const [needs, setNeeds] = useState(initialNeeds);
  const [pending, setPending] = useState(initialPending);
  const [busy, setBusy] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [code, setCode] = useState("");

  const verifying = needs.includes("email_code");

  async function send(step: NativeAppleSignUpStep) {
    setBusy(true);
    setErrorCode(null);
    setResent(false);
    const result = await continueNativeAppleSignUp(
      (input, init) => fetch(input, init),
      pending,
      step,
    );
    if (result.status === "ticket") {
      onTicket(result.ticket);
      return;
    }
    setBusy(false);
    if (result.status === "needs") {
      setPending(result.pending);
      setNeeds(result.needs);
      if (step.action === "resend") {
        setResent(true);
        setCode("");
      }
      return;
    }
    onFailure(result.code);
    setErrorCode(result.code);
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) {
      return;
    }
    if (verifying) {
      void send({ action: "verify", code });
      return;
    }
    void send({
      action: "details",
      email: needs.includes("email") ? email : null,
      firstName: needs.includes("name") ? firstName : null,
      lastName: needs.includes("name") ? lastName : null,
    });
  }

  const expired = errorCode === "pending_expired";

  return (
    <div className="fixed inset-0 z-[10050] flex items-end justify-center bg-black/60 px-4 pt-[max(env(safe-area-inset-top,0px),var(--safe-area-inset-top,0px))] pb-[calc(1rem+max(env(safe-area-inset-bottom,0px),var(--safe-area-inset-bottom,0px)))] sm:items-center">
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-2xl border border-(--mk-cream-08) bg-(--mk-ink-soft) p-5 shadow-[0_12px_40px_rgba(0,0,0,0.5)]"
      >
        <h2 id={titleId} className="text-lg font-semibold text-(--mk-cream)">
          {verifying ? t("appleSignUpCodeTitle") : t("appleSignUpTitle")}
        </h2>
        <p className="mt-1 text-sm leading-normal text-(--mk-cream-75)">
          {verifying
            ? email
              ? t("appleSignUpCodeBody", { email })
              : t("appleSignUpCodeBodyNoEmail")
            : t("appleSignUpDetailsBody")}
        </p>

        <div className="mt-4 space-y-3">
          {verifying ? (
            <div>
              <label htmlFor={`${fieldId}-code`} className={LABEL_CLASS}>
                {t("appleSignUpCodeLabel")}
              </label>
              <input
                id={`${fieldId}-code`}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={12}
                required
                autoFocus
                disabled={busy || expired}
                className={`${INPUT_CLASS} tracking-[0.3em]`}
              />
            </div>
          ) : (
            <>
              {needs.includes("name") ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor={`${fieldId}-first`} className={LABEL_CLASS}>
                      {t("appleSignUpFirstName")}
                    </label>
                    <input
                      id={`${fieldId}-first`}
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      autoComplete="given-name"
                      maxLength={100}
                      required
                      autoFocus
                      disabled={busy || expired}
                      className={INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label htmlFor={`${fieldId}-last`} className={LABEL_CLASS}>
                      {t("appleSignUpLastName")}
                    </label>
                    <input
                      id={`${fieldId}-last`}
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      autoComplete="family-name"
                      maxLength={100}
                      required
                      disabled={busy || expired}
                      className={INPUT_CLASS}
                    />
                  </div>
                </div>
              ) : null}
              {needs.includes("email") ? (
                <div>
                  <label htmlFor={`${fieldId}-email`} className={LABEL_CLASS}>
                    {t("appleSignUpEmail")}
                  </label>
                  <input
                    id={`${fieldId}-email`}
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={t("appleSignUpEmailPlaceholder")}
                    autoComplete="email"
                    autoCapitalize="none"
                    maxLength={254}
                    required
                    autoFocus={!needs.includes("name")}
                    disabled={busy || expired}
                    className={INPUT_CLASS}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>

        {errorCode ? (
          <p role="alert" className="mt-3 text-sm leading-normal text-[#FF9082]">
            {t(`appleSignUpErrors.${appleSignUpErrorKey(errorCode)}`)}
          </p>
        ) : resent ? (
          <p role="status" className="mt-3 text-sm text-(--mk-cream-75)">
            {t("appleSignUpResent")}
          </p>
        ) : null}

        <div className="mt-5 flex flex-col gap-2">
          {expired ? null : (
            <button
              type="submit"
              disabled={busy}
              className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl bg-(--mk-gold) px-4 text-sm font-semibold text-(--mk-ink) transition-colors hover:bg-(--mk-gold-light) disabled:cursor-progress disabled:opacity-70"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {verifying ? t("appleSignUpVerify") : t("appleSignUpContinue")}
            </button>
          )}
          {verifying && !expired ? (
            <button
              type="button"
              onClick={() => void send({ action: "resend" })}
              disabled={busy}
              className="h-10 cursor-pointer rounded-xl text-sm font-medium text-(--mk-gold) transition-colors hover:text-(--mk-gold-light) disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t("appleSignUpResend")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="h-10 cursor-pointer rounded-xl text-sm font-medium text-(--mk-cream-50) transition-colors hover:text-(--mk-cream-75) disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t("appleSignUpCancel")}
          </button>
        </div>
      </form>
    </div>
  );
}
